import {
  type ExtendedOptions,
  FetchError,
  FetchErrorReason,
} from '@cdc-internal/fe-toolbox/libs/fetcher'
import { isCIEnv, isProductionBuild, isServerEnv } from '@cdc-internal/fe-toolbox/utils/env'

import { showErrorNotification } from '@/components/basic/notifications'
import { logger } from '@/configs'
import { AppEnv, currentAppEnv, isLocalRuntime } from '@/configs/env'
import type { FetcherCustomData } from '@/types/api'
import { BackendApiErrorResponseBody, StandardApiResponseBody } from '@/types/api'
import { getRawTranslator } from '@/utils/localization'

export const logFetchError = (error: FetchError, url: string) =>
  logger.error('Failed request:', url, error?.toString())

// Timeout in BFF calling BE APIs will return a 500 status code with message - "Timed out in xxx ms"
export const isTimeoutFetchError = (error: FetchError) =>
  error.reason === FetchErrorReason.Timeout ||
  (error.responseParsedBody as StandardApiResponseBody)?.message?.includes('Timed out')

let showingTimeoutNotification = false
let showingNetworkErrorNotification = false
let showingServerErrorNotification = false
export function handleClientFetchError(
  error: FetchError,
  url: string,
  extendedOptions: Omit<ExtendedOptions, 'customData'> & { customData?: FetcherCustomData },
) {
  logFetchError(error, url)

  const t = getRawTranslator()

  // Show notification for timeout and network error
  if (isTimeoutFetchError(error)) {
    // Prevent showing multiple notifications triggered by multiple timeout errors
    if (showingTimeoutNotification) return
    showingTimeoutNotification = true
    return showErrorNotification({
      title: t('error.requestTimeOut'),
      onClose: () => {
        showingTimeoutNotification = false
      },
    })
  }

  if (error.reason === FetchErrorReason.NetworkError) {
    if (showingNetworkErrorNotification) return
    showingNetworkErrorNotification = true
    return showErrorNotification({
      title: t('error.networkError'),
      message: t('error.checkNetworkAgain'),
      onClose: () => {
        showingNetworkErrorNotification = false
      },
    })
  }

  if (
    extendedOptions.customData?.enableServerErrorNotification &&
    error.reason === FetchErrorReason.ResponseError &&
    error.statusCode! >= 500
  ) {
    if (showingServerErrorNotification) return

    showingServerErrorNotification = true
    return showErrorNotification({
      title: t('error.anErrorOccurred'),
      onClose: () => {
        showingServerErrorNotification = false
      },
    })
  }
}

export function handleFetcherResponseError<T extends BackendApiErrorResponseBody>(
  error: unknown,
  callback: (body: T) => void,
) {
  if (
    error instanceof FetchError &&
    error.reason === FetchErrorReason.ResponseError &&
    error.responseParsedBody
  ) {
    callback((error.responseParsedBody as StandardApiResponseBody<T>).data)
  }
}

export enum Version {
  V1 = 'v1',
  V2 = 'v2',
  V3 = 'v3',
}

interface ApiConfig {
  prefix: string
  serverDomain: string
  serverDomainInternal?: string
  version?: Version
  withVersion?: boolean
}

export function getApiRequestBaseUrl(config: ApiConfig) {
  const {
    serverDomain,
    serverDomainInternal,
    version = Version.V1,
    withVersion = true,
    prefix,
  } = config
  const useInternalDomain =
    isServerEnv &&
    isProductionBuild &&
    // CI will be set to 'true' in CI build time, and undefined in production server runtime
    !isCIEnv &&
    !isLocalRuntime &&
    !!serverDomainInternal &&
    // adev cluster can't connect to asta local host, so use public host instead
    currentAppEnv !== AppEnv.Dev

  const domain = useInternalDomain ? serverDomainInternal : serverDomain
  const protocol = useInternalDomain ? 'http://' : 'https://'

  const baseUrl = `${protocol}${domain}${prefix}${withVersion ? `/${version}` : ''}`

  return baseUrl
}
